import { useRef } from 'react';
import { heroAssets } from '@/mocks/site';
import useHeroZoom from '@/pages/home/components/hero/useHeroZoom';

/*
 * Layered hero driven by one GSAP ScrollTrigger timeline (see useHeroZoom):
 *  - background: workshop (IMAGE B), revealed through the dark intake
 *  - foreground: red car (IMAGE A), zooms into the air intake
 *  - ui:         centered cover text, fades independently
 */
export default function Hero() {
  const section = useRef<HTMLElement>(null);
  const fgImg = useRef<HTMLImageElement>(null);
  const bgImg = useRef<HTMLImageElement>(null);
  useHeroZoom({ section, fgImg, bgImg });

  return (
    <section ref={section} id="hero" data-hero-zoom className="relative h-[100svh] min-h-[640px] max-h-[1080px] overflow-hidden bg-[#0B0C10]">
      <div data-hero-layer="background" className="hidden md:block absolute inset-0 opacity-0 bg-[#0B0C10] will-change-transform [backface-visibility:hidden]" aria-hidden="true">
        <img ref={bgImg} src={heroAssets.background} alt="" decoding="sync" className="w-full h-full object-cover object-center" />
        {/* Dark aperture anchored on intake B; scales with the workshop during pull-out */}
        <div
          data-hero-bg-shade
          className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_var(--bx,66%)_var(--by,60%),#050505_0%,#050505_6%,rgba(5,5,5,0.9)_12%,transparent_26%)]"
        />
      </div>

      <div data-hero-layer="foreground" className="absolute inset-0 bg-[#0B0C10] will-change-transform [backface-visibility:hidden]">
        <img
          ref={fgImg}
          src={heroAssets.foreground}
          alt="Red performance car front bumper and air intake"
          title="Red performance car — Redline Garage auto repair"
          className="w-full h-full object-cover object-[78%_center] md:object-[72%_center] lg:object-center"
        />
        {/* Readability only: light top fade for nav + soft localized veil behind the headline */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_45%_28%_at_50%_50%,rgba(0,0,0,0.38),transparent_75%)]" />
        {/* Dark-match veil anchored on the intake; scales with the car */}
        <div
          data-hero-veil
          className="absolute inset-0 opacity-0 pointer-events-none bg-[radial-gradient(circle_at_var(--fx,80%)_var(--fy,61%),#050505_0%,#050505_9%,rgba(5,5,5,0.85)_16%,transparent_30%)]"
        />
      </div>

      <div data-hero-bridge className="hidden md:block absolute inset-0 bg-[#050505] opacity-0 pointer-events-none" aria-hidden="true" />

      <div data-hero-title-b className="hidden md:flex absolute inset-0 z-10 px-4 md:px-8 flex items-center justify-center text-center pointer-events-none">
        <div className="relative w-full max-w-[1100px]">
        <div data-hero-b-backing aria-hidden="true" className="opacity-0 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -z-10 w-[120%] max-w-[1200px] h-[260%] pointer-events-none bg-[radial-gradient(ellipse_closest-side_at_50%_50%,rgba(5,5,7,0.42)_0%,rgba(5,5,7,0.26)_45%,rgba(5,5,7,0.08)_75%,transparent_100%)] blur-[6px]" />
        <p data-hero-b-item className="opacity-0 text-[10px] md:text-[11px] font-medium uppercase tracking-[0.36em] text-white/80">
          Inside Redline Garage
        </p>
        <h2 data-hero-b-item className="opacity-0 mt-5 font-sans font-semibold uppercase text-[#F5F5F6] text-[28px] sm:text-[32px] lg:text-[44px] leading-[1.15] tracking-[0.08em] lg:whitespace-nowrap">
          The work behind every drive.
        </h2>
        <p data-hero-b-item className="opacity-0 mt-4 text-[13px] md:text-[15px] font-light text-white/75 tracking-[0.02em]">
          Expert hands. Precision at every step.
        </p>
        </div>
      </div>

      <div data-hero-layer="ui" className="relative z-10 h-full w-full px-4 md:px-8 flex items-center justify-center text-center">
        <div className="relative w-full max-w-[1100px]">
          <div aria-hidden="true" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -z-10 w-[120%] max-w-[1200px] h-[260%] pointer-events-none bg-[radial-gradient(ellipse_closest-side_at_50%_50%,rgba(5,5,7,0.42)_0%,rgba(5,5,7,0.26)_45%,rgba(5,5,7,0.08)_75%,transparent_100%)] blur-[6px]" />
          <p className="text-[10px] md:text-[11px] font-medium uppercase tracking-[0.36em] text-white/80">
            Automotive service &amp; repair
          </p>
          <h1 className="mt-5 font-sans font-semibold uppercase text-white text-[26px] sm:text-[34px] lg:text-[48px] leading-[1.15] tracking-[0.08em] lg:whitespace-nowrap">
            Precision in every repair.
          </h1>
          <p className="mt-4 text-[13px] md:text-[15px] font-light text-white/75 tracking-[0.02em]">
            Expert care for the road ahead.
          </p>
        </div>
      </div>
    </section>
  );
}